import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import CryptoTicker from '../components/CryptoTicker';
import LangSwitch from '../components/LangSwitch';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  Shield, ArrowLeft, LogOut, TrendingUp, Wallet, Calendar, ArrowUpRight, ArrowDownRight, PieChart as PieIcon, BarChart3, Download
} from 'lucide-react';

const COLORS = ['#7C3AED', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

export default function ClientReport() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { t } = useI18n();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await clientsAPI.get(clientId);
        setClient(res.data);
      } catch {
        toast.error(t('report_not_found'));
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, navigate, t]);

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current || !client) return;
    setExporting(true);
    toast.info(t('report_pdf_generating'));
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#0B0C15', useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfW / canvas.width, pdfH / canvas.height);
      const w = canvas.width * ratio;
      const totalPages = Math.ceil((canvas.height * ratio) / pdfH);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -(page * pdfH), w, canvas.height * ratio);
      }
      pdf.save(`ALGO_PREMIUM_${client.client_name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 7)}.pdf`);
      toast.success(t('report_pdf_success'));
    } catch {
      toast.error(t('report_pdf_error'));
    } finally {
      setExporting(false);
    }
  }, [client, t]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0C15' }}>
      <div className="text-[#94A3B8]">{t('report_loading')}</div>
    </div>
  );

  if (!client) return null;

  const sortedTx = [...(client.transactions || [])].sort((a, b) => {
    const [dA, mA, yA] = a.date.split('/').map(Number);
    const [dB, mB, yB] = b.date.split('/').map(Number);
    return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
  });

  const totalDeposits = sortedTx.filter(t => t.type === 'deposit').reduce((s, t) => s + t.amount, 0);
  const totalWithdrawals = sortedTx.filter(t => t.type === 'withdrawal').reduce((s, t) => s + t.amount, 0);
  const pieData = (client.allocations || []).map(a => ({ name: a.name, value: a.percentage }));

  return (
    <div className="min-h-screen" style={{ background: '#0B0C15' }} data-testid="client-report">
      <CryptoTicker />

      <header className="border-b border-[#2D2E3D] bg-[#151621]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button data-testid="back-button" variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight text-white">ALGO PREMIUM</span>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="export-pdf-button" size="sm" className="bg-[#1E1F2E] hover:bg-[#2D2E3D] text-white border border-[#2D2E3D] text-xs" onClick={handleExportPDF} disabled={exporting}>
              <Download className="w-3.5 h-3.5 mr-1" /> {exporting ? 'Export...' : 'PDF'}
            </Button>
            <LangSwitch />
            <span className="text-sm text-[#94A3B8]">{user?.username}</span>
            <Button data-testid="report-logout-button" variant="ghost" size="icon" className="text-[#64748B] hover:text-white" onClick={() => { logout(); navigate('/'); }}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div ref={reportRef}>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-10 opacity-0 animate-fade-in-up">
          <p className="text-sm text-[#7C3AED] font-semibold tracking-widest uppercase mb-2">{t('report_title')}</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-1">{client.client_name}</h1>
          <p className="text-[#94A3B8] text-sm">{t('report_member_since')} {client.date_joined} &middot; {t('report_last_update')}: {client.balance_date}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {[
            { label: t('report_total_deposit'), value: fmt(client.deposit), icon: Wallet, color: '#0EA5E9' },
            { label: t('report_balance'), value: fmt(client.balance), icon: TrendingUp, color: '#7C3AED', sub: `${t('report_at')} ${client.balance_date}` },
            { label: t('report_return'), value: fmt(client.return_amount), icon: ArrowUpRight, color: '#10B981', sub: `${t('report_cumul')}: ${client.return_pct}%` },
            { label: t('report_monthly'), value: `${(client.monthly_return_pct || 0).toFixed(2)}%`, icon: Calendar, color: '#0EA5E9', sub: t('report_monthly_sub') },
            { label: t('report_new_balance'), value: client.new_balance ? fmt(client.new_balance) : '—', icon: BarChart3, color: '#F59E0B', sub: client.new_balance_date ? `${t('report_at')} ${client.new_balance_date}` : '' },
          ].map((m, i) => (
            <Card key={i} className={`bg-[#151621] border-[#2D2E3D] opacity-0 animate-fade-in-up stagger-${i + 1}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-[#94A3B8]">{m.label}</span>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${m.color}15` }}>
                    <m.icon className="w-4 h-4" style={{ color: m.color }} />
                  </div>
                </div>
                <div className="text-2xl font-bold text-white font-mono tracking-tight">{m.value}</div>
                {m.sub && <p className="text-xs text-[#64748B] mt-1 font-mono">{m.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Allocation + Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
          <Card className="bg-[#151621] border-[#2D2E3D] lg:col-span-5 opacity-0 animate-fade-in-up stagger-1">
            <CardHeader className="border-b border-[#2D2E3D]/50">
              <CardTitle className="text-base text-white flex items-center gap-2">
                <PieIcon className="w-4 h-4 text-[#7C3AED]" /> {t('report_allocation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {pieData.length > 0 ? (
                <div className="flex flex-col items-center">
                  <div className="w-48 h-48 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" stroke="none">
                          {pieData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#151621', border: '1px solid #2D2E3D', borderRadius: '8px', color: '#fff', fontSize: '12px' }} formatter={(value) => [`${value}%`, '']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full space-y-2">
                    {pieData.map((item, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-sm text-[#94A3B8]">{item.name}</span>
                        </div>
                        <span className="text-sm font-mono text-white">{item.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[#64748B] text-sm text-center py-6">{t('report_no_alloc')}</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#151621] border-[#2D2E3D] lg:col-span-7 opacity-0 animate-fade-in-up stagger-2">
            <CardHeader className="border-b border-[#2D2E3D]/50">
              <CardTitle className="text-base text-white">{t('report_summary')}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between py-3 border-b border-[#2D2E3D]/30">
                <span className="text-sm text-[#94A3B8]">{t('report_account_type')}</span>
                <span className="text-sm text-white max-w-xs text-right">{client.account_type || '—'}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#2D2E3D]/30">
                <span className="text-sm text-[#94A3B8]">{t('report_tx_count')}</span>
                <span className="text-sm font-mono text-white">{client.transactions?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#2D2E3D]/30">
                <span className="text-sm text-[#94A3B8]">{t('report_total_deposits')}</span>
                <span className="text-sm font-mono text-[#10B981]">+{fmt(totalDeposits)}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#2D2E3D]/30">
                <span className="text-sm text-[#94A3B8]">{t('report_total_withdrawals')}</span>
                <span className="text-sm font-mono text-[#F59E0B]">-{fmt(totalWithdrawals)}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#2D2E3D]/30">
                <span className="text-sm text-white font-semibold">{t('report_perf_cumul')}</span>
                <div className="text-right">
                  <span className="text-lg font-mono font-bold text-[#10B981]">{client.return_pct}%</span>
                  <p className="text-xs font-mono text-[#64748B]">{fmt(client.return_amount)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-white font-semibold">{t('report_perf_monthly')}</span>
                <span className="text-lg font-mono font-bold text-[#0EA5E9]">{(client.monthly_return_pct || 0).toFixed(2)}%</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction History */}
        <Card className="bg-[#151621] border-[#2D2E3D] mb-10 opacity-0 animate-fade-in-up stagger-3">
          <CardHeader className="border-b border-[#2D2E3D]/50">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0EA5E9]" /> {t('report_tx_history')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1E1F2E] hover:bg-[#1E1F2E] border-[#2D2E3D]/50">
                  <TableHead className="text-[#94A3B8] font-medium px-6 py-3">{t('report_date')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('report_type')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium text-right pr-6">{t('report_amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTx.map((tx, i) => (
                  <TableRow key={tx.id || i} className="border-[#2D2E3D]/50 hover:bg-[#1E1F2E]/50 transition-colors">
                    <TableCell className="px-6 py-3 font-mono text-sm text-white">{tx.date}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {tx.type === 'deposit' ? <ArrowDownRight className="w-3.5 h-3.5 text-[#10B981]" /> : <ArrowUpRight className="w-3.5 h-3.5 text-[#F59E0B]" />}
                        <Badge className={tx.type === 'deposit' ? 'bg-[#10B981]/15 text-[#10B981] border-0 text-xs' : 'bg-[#F59E0B]/15 text-[#F59E0B] border-0 text-xs'}>
                          {tx.type === 'deposit' ? t('report_deposit') : t('report_withdrawal')}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className={`text-right pr-6 font-mono text-sm font-medium ${tx.type === 'deposit' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                      {tx.type === 'deposit' ? '+' : '-'}{fmt(tx.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 border-t border-[#2D2E3D]/50">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-6 h-6 rounded bg-[#7C3AED] flex items-center justify-center">
              <Shield className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-bold text-white tracking-tight">ALGO PREMIUM</span>
          </div>
          <p className="text-xs text-[#64748B]">Strategy Intelligence Solutions &middot; AI Consulting &middot; si-solutions.ai</p>
          <p className="text-xs text-[#64748B] mt-1">Blockchain / Crypto Consulting</p>
        </div>
      </main>
      </div>
    </div>
  );
}
